import { useState, useEffect } from "react";
import {
  Breadcrumbs,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import * as services from "./../../services/saleng";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const AddBranch = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    shopName: "",
    branchName: "",
    branchCode: "",
    taxNumber: "",
    phoneNumber: "",
    email: "",
    address: "",
    image: "",
    priceAnnouncementType: "HEAD_OFFICE",
  });

  const [errors, setErrors] = useState({});
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isFirstBranch, setIsFirstBranch] = useState(false);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*", // รองรับเฉพาะไฟล์ภาพ
  });

  const validate = () => {
    const newErrors = {};
    if (!formData.shopName.trim()) newErrors.shopName = "กรุณากรอกชื่อร้าน";
    if (!formData.branchName.trim()) newErrors.branchName = "กรุณากรอกชื่อสาขา";
    if (!formData.branchCode.trim()) newErrors.branchCode = "กรุณากรอกรหัสสาขา";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "", // ลบข้อความ error เมื่อผู้ใช้เริ่มกรอกข้อมูล
    }));
  };

  const handleSave = async (back) => {
    if (!validate()) {
      return;
    }

    try {
      let imageUrl = null;
      if (imageFile) {
        const uploadResponse = await services.uploadFile(
          imageFile,
          "company_logo",
        );
        imageUrl = uploadResponse.data?.file || null;
      }

      const updatedFormData = {
        ...formData,
        name: formData.branchName,
        image: imageUrl,
      };

      await services.createBranch(updatedFormData);
      if (back) {
        navigate("/branches");
      } else {
        window.location.reload();
      }
    } catch (error) {
      toast.error(error?.message || "ไม่สามารถบันทึกสาขาได้");
      console.error("Error creating branch:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const shopResponse = await services.getShop();
        const shopData = shopResponse.data.data;

        console.log("shopData", shopData);
        const branches = Array.isArray(shopData.branches)
          ? shopData.branches
          : [];
        const firstBranch = branches.length === 0;
        const branchCode = firstBranch
          ? "B01"
          : `B${String(branches.length + 1).padStart(2, "0")}`;
        console.log(branchCode);

        setIsFirstBranch(firstBranch);
        setFormData((prev) => ({
          ...prev,
          shopName: shopData.name,
          branchName: firstBranch ? "สาขาหลัก" : prev.branchName,
          branchCode: branchCode,
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2, mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          หน้าแรก
        </Link>
        <Link underline="hover" color="inherit" href="/branches">
          จัดการสาขา
        </Link>
        <Typography color="text.primary">เพิ่มสาขา</Typography>
      </Breadcrumbs>

      <Toaster position="bottom-left" reverseOrder={false} />
      <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
        เพิ่มสาขา
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Box
          component="form"
          sx={{ "& .MuiTextField-root": { mt: 1, mb: 1, width: "100%" } }}
          noValidate
        >
          <Typography>ชื่อร้าน</Typography>
          <TextField
            id="shopName"
            name="shopName"
            variant="outlined"
            value={formData.shopName}
            placeholder="ชื่อร้าน"
            size="small"
          />
          <Typography>ชื่อสาขา*</Typography>
          <TextField
            required
            id="branchName"
            name="branchName"
            variant="outlined"
            value={formData.branchName}
            placeholder="ชื่อสาขา"
            onChange={handleChange}
            disabled={isFirstBranch}
            error={!!errors.branchName}
            helperText={
              errors.branchName ||
              (isFirstBranch ? "สาขาแรกจะเป็นสาขาหลักและแก้ไขไม่ได้" : "")
            }
            size="small"
          />
          <Typography>รหัสสาขา</Typography>
          <TextField
            id="branchCode"
            name="branchCode"
            placeholder="รหัสสาขา"
            variant="outlined"
            value={formData.branchCode}
            disabled={isFirstBranch}
            size="small"
          />
          <Typography>เลขประจำตัวผู้เสียภาษี</Typography>
          <TextField
            id="taxNumber"
            name="taxNumber"
            placeholder="เลขประจำตัวผู้เสียภาษี"
            variant="outlined"
            value={formData.taxNumber}
            onChange={handleChange}
            error={!!errors.taxNumber}
            helperText={errors.taxNumber}
            size="small"
          />
          <Typography>เบอร์โทรศัพท์</Typography>
          <TextField
            id="phoneNumber"
            name="phoneNumber"
            placeholder="เบอร์โทรศัพท์"
            variant="outlined"
            value={formData.phoneNumber}
            onChange={handleChange}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            size="small"
          />
          <Typography>ที่อยู่</Typography>
          <TextField
            id="address"
            name="address"
            placeholder="ที่อยู่"
            variant="outlined"
            value={formData.address}
            onChange={handleChange}
            error={!!errors.address}
            helperText={errors.address}
            size="small"
          />
          <Typography>โลโก้ร้าน</Typography>
          <Box
            {...getRootProps()}
            sx={{
              width: 300,
              height: 300,
              border: "2px dashed gray",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              mt: 1,
              cursor: "pointer",
              bgcolor: isDragActive ? "lightgray" : "white",
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body1">
              {isDragActive
                ? "ลากและวางไฟล์ที่นี่..."
                : "ลากและวางหรือคลิกเพื่ออัปโหลดโลโก้"}
            </Typography>
            <IconButton color="primary" sx={{ mt: 1 }}>
              <CloudUploadIcon />
            </IconButton>
            {image && (
              <Box mt={2}>
                <img
                  src={image}
                  alt="Logo Preview"
                  //    style={{ width: 100, height: 100, objectFit: 'contain' }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <ButtonGroup
          variant="contained"
          aria-label="Basic button group"
          sx={{ mt: 2 }}
          color="success"
        >
          <Button onClick={() => handleSave(true)} color="">
            บันทึกและกลับ
          </Button>
          <Button onClick={() => handleSave(false)}>บันทึก</Button>
        </ButtonGroup>
      </Paper>
    </>
  );
};

export default AddBranch;

// import React, { useState } from "react";
// import { useDropzone } from "react-dropzone";
// import { Box, Typography, Button, IconButton } from "@mui/material";
// import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// const LogoDropzone = () => {
//   const [image, setImage] = useState(null);

//   const onDrop = (acceptedFiles) => {
//     const file = acceptedFiles[0];
//     if (file && file.type.startsWith("image/")) {
//       const reader = new FileReader();
//       reader.onload = () => {
//         setImage(reader.result);
//       };
//       reader.readAsDataURL(file);
//     } else {
//       alert("กรุณาเลือกไฟล์รูปภาพเท่านั้น");
//     }
//   };

//   const { getRootProps, getInputProps, isDragActive } = useDropzone({
//     onDrop,
//     accept: "image/*", // รองรับเฉพาะไฟล์ภาพ
//   });

//   return (
//     <Box
//       {...getRootProps()}
//       sx={{
//         width: 300,
//         height: 300,
//         border: "2px dashed gray",
//         borderRadius: 2,
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         justifyContent: "center",
//         p: 2,
//         cursor: "pointer",
//         bgcolor: isDragActive ? "lightgray" : "white",
//       }}
//     >
//       <input {...getInputProps()} />
//       <Typography variant="body1">
//         {isDragActive ? "ลากและวางไฟล์ที่นี่..." : "ลากและวางหรือคลิกเพื่ออัปโหลดโลโก้"}
//       </Typography>
//       <IconButton
//         color="primary"
//         sx={{ mt: 1 }}
//       >
//         <CloudUploadIcon />
//       </IconButton>
//       {image && (
//         <Box mt={2}>
//           <img
//             src={image}
//             alt="Logo Preview"
//             //    style={{ width: 100, height: 100, objectFit: 'contain' }}
//           />
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default LogoDropzone;
